import { useState, useRef } from "react";
import { useDirectUpload } from "../../hooks/useDirectUpload";
import { Upload, X, CheckCircle, AlertCircle } from "lucide-react";

interface DirectUploadInputProps {
  onUploadComplete: (signedBlobId: string) => void;
  onUploadError?: (error: Error) => void;
  accept?: string;
  label?: string;
  required?: boolean;
  className?: string;
  error?: string;
}

export default function DirectUploadInput({
  onUploadComplete,
  onUploadError,
  accept = "image/*",
  label = "Upload File",
  required = false,
  className = "",
  error: externalError,
}: DirectUploadInputProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedBlobId, setUploadedBlobId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    uploadFile,
    uploading,
    progress,
    error: uploadError,
  } = useDirectUpload();

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setUploadedBlobId(null);

    try {
      const signedBlobId = await uploadFile(file);
      setUploadedBlobId(signedBlobId);
      onUploadComplete(signedBlobId);
    } catch (error) {
      if (onUploadError) {
        onUploadError(error as Error);
      }
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setUploadedBlobId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const displayError = externalError || uploadError;

  return (
    <div className={`form-control ${className}`}>
      <label className="label">
        <span className="label-text">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </span>
      </label>

      <div className="space-y-2">
        {/* File Input Button */}
        {!selectedFile && (
          <label
            className={`btn btn-outline w-full ${
              displayError ? "btn-error" : ""
            }`}
          >
            <Upload className="w-4 h-4 mr-2" />
            Choose File
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={accept}
              onChange={handleFileSelect}
              disabled={uploading}
              required={required}
            />
          </label>
        )}

        {/* Selected File Display */}
        {selectedFile && (
          <div
            className={`alert ${
              uploadedBlobId
                ? "alert-success"
                : uploading
                ? "alert-info"
                : "alert-warning"
            }`}
          >
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {uploadedBlobId ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : uploading ? (
                    <span className="loading loading-spinner loading-sm"></span>
                  ) : (
                    <Upload className="w-5 h-5" />
                  )}
                  <div>
                    <div className="font-medium">{selectedFile.name}</div>
                    <div className="text-sm opacity-70">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                {!uploading && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="btn btn-ghost btn-sm btn-circle"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Progress Bar */}
              {uploading && (
                <div className="mt-2">
                  <progress
                    className="progress progress-primary w-full"
                    value={progress}
                    max="100"
                  ></progress>
                  <div className="text-xs text-center mt-1">{progress}%</div>
                </div>
              )}

              {/* Success Message */}
              {uploadedBlobId && (
                <div className="text-sm mt-1">Upload complete!</div>
              )}
            </div>
          </div>
        )}

        {/* Error Display */}
        {displayError && (
          <div className="alert alert-error">
            <AlertCircle className="w-5 h-5" />
            <span>{displayError}</span>
          </div>
        )}
      </div>

      {/* Help Text */}
      {!displayError && !selectedFile && (
        <label className="label">
          <span className="label-text-alt">Accepted formats: {accept}</span>
        </label>
      )}
    </div>
  );
}
